import React from 'react'
import { useHistory } from 'react-router-dom'

import { customFolder } from '../../../../../constants/constants'
import { RoutePath } from '../../../../../router/routesConfig'

import useLookupService from '../../../../../services/lookup/useLookupService'
import useWebService from '../../../../../services/web/useWebService'
import useLoading from '../../../../../control/loading/useLoading'
import useManageProjectAction from '../../../../../stores/manageProject/useManageProjectAction'
import useManageProjectStore from '../../../../../stores/manageProject/useManageProjectStore'
import useAppFolderAndTabAction from '../../../../../stores/appFolderAndTab/appFolderAndTabAction'
import useFolderAndTabStore from '../../../../../stores/appFolderAndTab/appFolderAndTabStore'
import useFolderAndTab from '../../useFolderAndTab'

import useTranslate from '../../../../../i18n/useTranslate'
import checkPermission from '../../../../../utilities/checkPermission'

import * as verifyDocumentSubmittedWithdrawType from './verifyDocumentSubmittedWithdrawType'
import { DialogType } from '../../../../../control/dialog/dialog'
import constSubmittedType from '../../../../../constants/submittedType'
import * as REQServiceTypes from '../../../../../services/web/types/requestWebServiceTypes'
import * as RESServiceTypes from '../../../../../services/web/types/responseWebServiceTypes'
import { responseWebServiceTypes } from '../../../../../services/web/types'
import * as DateUtils from '../../../../../utilities/date'

import { VerifyDocSubmittedWithdraw } from './verifyDocumentSubmittedWithdraw'
import { validateModeAccordionError } from '../../../../../utilities/validate'

const useVerifyDocumentSubmittedWithdraw = (props: VerifyDocSubmittedWithdraw) => {
    const { onSaveComplete } = props
    const [initialValues, setInitialValues] = React.useState<any>({})
    const [initialUploadFile, setInitialUploadFile] = React.useState<any>()
    const [projectPageData, setProjectPageData] = React.useState<RESServiceTypes.GetProjectWithdrawResponse>()
    const [configDialog, setConfigDialog] =
        React.useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()

    const formRef = React.useRef<any>()
    const dialogRef = React.useRef<Record<string, Function>>()

    const loadingHook = useLoading()
    const callLookup = useLookupService()
    const callWebService = useWebService()
    const history = useHistory()
    const translate = useTranslate()
    const checkPermissionHook = checkPermission()
    const tabAndFolderHook = useFolderAndTab()
    const folderAndTabStore = useFolderAndTabStore()
    const appFolderAndTabAction = useAppFolderAndTabAction()
    const manageProjectAction = useManageProjectAction()
    const manageProjectStore = useManageProjectStore()
    const { folderSelected } = folderAndTabStore
    const isViewMode = checkPermissionHook.chkProjectStepViewMode(manageProjectStore.projectData)
    const formHistoryRef = React.useRef<Record<string, any>>({})

    React.useEffect(() => {
        if (manageProjectStore.projectData) {
            console.log('####Page: Project Withdraw####')
            loadProjectPageData()
        }
    }, [manageProjectStore.projectData?.submittedID])

    const getDocumentTopicLut = async (irb: number, submitTypeID: number) => {
        const res = await callLookup.callDocumentTopicLut(irb, submitTypeID)
        if (res.status === 1) {
            return res.result
        } else {
            return []
        }
    }

    const getSaveServiceParameter = (isDraft: boolean) => {
        const values = formRef.current?.values
        let reviewerUserID = null
        if (values?.reviewer?.value) {
            reviewerUserID = parseInt(values.reviewer.value)
        }
        const param: REQServiceTypes.SaveVerifyProjectWithdrawType = {
            projectID: manageProjectStore.projectData?.projectID ?? null,
            submittedID: manageProjectStore.projectData?.submittedID ?? null,
            isCompletely: values?.isCompletely ?? false,
            resubmitDocDate: DateUtils.dateForAPI(values?.resubmitDocDate) ?? null,
            verifyDocRemark: values?.remark ?? null,
            isDraft: isDraft,
            reviewerUserID: reviewerUserID,
        }
        return param
    }

    const loadProjectPageData = async () => {
        if (manageProjectStore.projectData?.submittedID) {
            initialProcessUpdate()
        } else {
            initialProcessCreate()
        }
    }

    const initialProcessCreate = () => {
        createInitialData()
        initialUpload()
    }

    const initialProcessUpdate = async () => {
        if (manageProjectStore.projectData?.submittedID) {
            const res = await callWebService.getProjectWithdraw(manageProjectStore.projectData.submittedID)
            if (res.status === 1) {
                setProjectPageData(res.result)

                createInitialData(res.result)
                initialUpload(res.result.documentInfo)
            } else {
                setProjectPageData(undefined)
            }
        }
    }

    const createInitialData = (initVal?: responseWebServiceTypes.GetProjectWithdrawResponse) => {
        const initialData: Record<string, any> = {
            withdrawCause: null,
            isCompletelyDoc: '1',
            isCompletely: true,
            resubmitDocDate: null,
            remark: null,
            documentAdditionalInfo: [],
        }
        if (initVal) {
            initialData.withdrawCause = initVal.reason
            initialData.isCompletelyDoc =
                initVal.isVerifyDocumentCompleted !== null ? (initVal.isVerifyDocumentCompleted ? '1' : '2') : null

            initialData.isCompletely = initVal.isVerifyDocumentCompleted
            initialData.remark = initVal.verifyDocRemark
            initialData.resubmitDocDate = initVal.resubmittedDate ? new Date(initVal.resubmittedDate) : null
            initialData.documentAdditionalInfo = initVal.documentAdditionalInfo ?? []
        }

        setInitialValues(initialData)
    }

    const initialUpload = async (
        initDocumentInfo?: responseWebServiceTypes.GetProjectWithdrawResponse['documentInfo'],
    ) => {
        if (manageProjectStore.projectData?.irb) {
            const lstDocumentTopic = await getDocumentTopicLut(
                manageProjectStore.projectData?.irb,
                constSubmittedType.withdrawalSubmitted,
            )
            manageProjectAction.setDocumentTopic(lstDocumentTopic)

            let uploadFileInit = lstDocumentTopic?.map((itm: any, idx: number) => {
                return {
                    uploadFileObj: {
                        fileList: [],
                        subTopicPageList: [],
                        documentTopicID: itm.id,
                        isDisplay: itm.isDisplay,
                        isRequired: itm.isRequired,
                    },
                }
            }) as Array<verifyDocumentSubmittedWithdrawType.UploadFileObjType>

            if (folderSelected.id !== customFolder.withdrawSubmitted.id) {
                uploadFileInit?.forEach((itm: verifyDocumentSubmittedWithdrawType.UploadFileObjType) => {
                    initDocumentInfo?.forEach((docInfo) => {
                        if (docInfo.topicID === itm.uploadFileObj.documentTopicID) {
                            if (docInfo.fileInfo?.length > 0) {
                                //@ts-ignore
                                itm.uploadFileObj.fileList.push({
                                    ...docInfo.fileInfo[0],
                                    fileID: docInfo.fileInfo[0].fileToken,
                                    documentTopicID: docInfo.fileInfo[0].topicID as number,
                                    fileToken: docInfo.fileInfo[0].fileToken, //fileToken
                                    filename: docInfo.fileInfo[0].filename,
                                    path: docInfo.fileInfo[0].path,
                                })
                            }

                            if (docInfo?.subtopic?.length > 0) {
                                //@ts-ignore
                                itm.uploadFileObj.subTopicPageList = docInfo.subtopic[0]
                            }
                        }
                    })
                })
            }
            //Filter การซ่อนแสดงหัวข้อเอกสาร ถ้าฝั่งสำนักงานให้ซ่อนหัวข้อเอกสาร แต่ว่าหัวข้อนั้นมีไฟล์อยู่ให้ทำการแสดงหัวข้อนั้นออกมา
            if (uploadFileInit?.length > 0) {
                uploadFileInit = uploadFileInit.filter((itm: any) => {
                    return itm.uploadFileObj.isDisplay || itm.uploadFileObj.fileList.length !== 0
                })
            }
            setInitialUploadFile(uploadFileInit)
        }
    }

    const processSaveData = async (
        param: REQServiceTypes.SaveVerifyProjectWithdrawType,
        changeTabOpt?: { idx: number; selectedInfo: any },
    ) => {
        try {
            dialogRef.current?.closeDialog()
            loadingHook.loadingOpen()
            const response = await callWebService.saveVerifyProjectWithdraw(param)

            if (changeTabOpt) {
                if (changeTabOpt.idx !== null && changeTabOpt.selectedInfo !== null) {
                    tabAndFolderHook.setTab(changeTabOpt.idx, changeTabOpt.selectedInfo)
                }
            } else {
                if (onSaveComplete) onSaveComplete(response.status, param.isDraft as boolean)
            }
        } catch (ex) {
            console.log('useVerifyDocumentProjectWithdraw.ts => processSaveData : ', ex)
        } finally {
            loadingHook.loadingClose()
        }
    }

    const onSubmit = (values: any) => {
        if (manageProjectStore.projectData?.projectID && manageProjectStore.projectData?.submittedID) {
            const isDraft: boolean = values.saveType === 'DRAFT'
            const saveParam = getSaveServiceParameter(isDraft)

            if (isDraft) {
                processSaveData(saveParam)
            } else {
                setConfigDialog({
                    title: translate.get('ALERT.TITLE.NOTIFICATION'),
                    content: translate.get('ALERT.CONTENT.NEED_TO_SAVE'),
                    type: 'alert-confirm',
                    onClickConfirm: () => {
                        processSaveData(saveParam)
                    },
                })

                dialogRef.current?.openDialog()
            }
        }
    }

    const callbackDirtyProcess = (tabIndex: number, newTabSelected: any) => {
        const isDraft: boolean = true
        const param = getSaveServiceParameter(isDraft)
        processSaveData(param, {
            idx: tabIndex,
            selectedInfo: newTabSelected,
        })
    }
    const onClickCancel = (dirty: boolean) => {
        if (dirty) {
            setConfigDialog({
                title: translate.get('ALERT.TITLE.WARNING'),
                content: translate.get('ALERT.CONTENT.NEED_TO_CANCEL'),
                type: 'alert-confirm',

                onClickConfirm: () => {
                    history.push(RoutePath.manageProject)
                },
            })
            dialogRef.current?.openDialog()
        } else {
            history.push(RoutePath.manageProject)
        }
    }
    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }
    const onDirtyChange = (formState: Record<string, any>) => {
        let isDirty: boolean = false
        if (!isViewMode) {
            isDirty = formState.dirty
        }
        const paramsDirty = {
            tabDirty: isDirty,
            callbackDirty: isDirty ? callbackDirtyProcess : undefined,
        }
        appFolderAndTabAction.setDirty(paramsDirty)
    }
    const onRefForm = (formProps: any) => {
        formRef.current = formProps
    }

    const confirmEditDocResult = (formProps: any, fieldNames: string[]) => {
        const isError = validateModeAccordionError(fieldNames, formProps)
        return new Promise((resolve, reject) => {
            if (!isError) {
                let isCompletelyDoc = formProps.values.isCompletelyDoc === '1'
                let sendEditData = null
                if (!isCompletelyDoc && formProps.values.resubmitDocDate) {
                    sendEditData = DateUtils.dateForAPI(formProps.values.resubmitDocDate) ?? null
                }
                const param = {
                    ...getEditDocParams(formHistoryRef.current['docResult']),
                    isCompletely: isCompletelyDoc,
                    resubmitDocDate: sendEditData,
                    //remark: isCompletelyDoc ? null : formProps.values?.remark ? formProps.values.remark : null,
                    remark: isCompletelyDoc ? null : formProps.values?.remarkWithFormat?.contentText ? formProps.values.remarkWithFormat.contentText : null,
                    remarkHTML: isCompletelyDoc ? null : formProps.values?.remarkWithFormat?.contentHtml ? formProps.values.remarkWithFormat.contentHtml : null,
                }
                processSaveData(param)
                    .then(() => {
                        handleEditDocResult(formProps)
                        resolve('')
                    })
                    .catch(() => {
                        reject('')
                    })
            } else {
                formProps.form.submit()
                reject('')
            }
        })
    }

    const handleEditDocResult = (formProps: any) => {
        formHistoryRef.current['docResult'] = formProps.values
    }

    const getEditDocParams = (formValues: any) => {
        let reviewerUserID = null
        if (formValues?.reviewer?.value) {
            reviewerUserID = parseInt(formValues.reviewer.value)
        }
        const param: REQServiceTypes.SaveVerifyProjectWithdrawType = {
            projectID: manageProjectStore.projectData?.projectID ?? null,
            submittedID: manageProjectStore.projectData?.submittedID ?? null,
            isCompletely: formValues?.isCompletely ?? false,
            resubmitDocDate: DateUtils.dateForAPI(formValues?.resubmitDocDate) ?? null,
            verifyDocRemark: formValues?.remark ?? null,
            isDraft: false,
            reviewerUserID: reviewerUserID,
        }
        return param
    }

    return {
        initialVal: {
            ...initialValues,
            uploadFile: initialUploadFile,
            documentAdditionalInfo: manageProjectStore.projectData?.documentAdditionalInfo,
        },
        initialUploadFile,
        uploadDocumentTopic: manageProjectStore.documentTopic,
        isViewMode: isViewMode,
        configDialog,
        projectData: manageProjectStore.projectData,
        onRefForm,
        onClickCancel,
        onSubmit,
        onRefDialog,
        onDirtyChange,
        confirmEditDocResult,
        handleEditDocResult,
    }
}
export default useVerifyDocumentSubmittedWithdraw
